import React from "react";
import { useParams, Link } from "react-router-dom";
import { POLES, INSCRIPTIONS_URL, QUEENS_SHOW, JDA_SHOW, COMPAGNIE_UNIVERS, PRESTATION_PHOTOS, PRESTATION_VIDEO } from "@/lib/data";
import { getSiteSettings } from "@/lib/site";
import { http } from "@/lib/api";
import { ArrowRight, Download, Sparkles } from "lucide-react";

export default function Pole() {
  const { slug } = useParams();
  const pole = POLES.find((p) => p.slug === slug);
  const [settings, setSettings] = React.useState({});
  const [videos, setVideos] = React.useState([]);
  React.useEffect(() => { getSiteSettings().then(setSettings).catch(() => {}); }, []);
  React.useEffect(() => {
    if (!pole) return;
    const link = document.querySelector("link[rel='icon']");
    if (!link) return;
    const prev = link.getAttribute("href");
    link.setAttribute("href", `/favicons/${pole.slug}.png`);
    return () => link.setAttribute("href", prev);
  }, [pole && pole.slug]);
  React.useEffect(() => {
    http.get("/gallery")
      .then((r) => setVideos((r.data || []).filter((i) => i.kind === "video" && i.pole === pole.name)))
      .catch(() => {});
  }, [pole && pole.slug]);
  if (!pole) return <div className="max-w-3xl mx-auto py-24 px-4">Pôle introuvable.</div>;
  const poleImg = settings.images?.[pole.slug] || pole.image;
  const videoGroups = [];
  videos.forEach((v) => {
    const name = v.group || "";
    let g = videoGroups.find((x) => x.name === name);
    if (!g) { g = { name, items: [] }; videoGroups.push(g); }
    g.items.push(v);
  });

  return (
    <div>
      <section className="relative h-[60vh] min-h-[420px] bg-black text-white he-grain overflow-hidden">
        <img src={poleImg} alt={pole.name} className="absolute inset-0 w-full h-full object-cover opacity-60 grayscale" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        <div className="relative max-w-7xl mx-auto h-full px-4 sm:px-6 flex flex-col justify-end pb-14">
          {pole.logo && (
            <img src={pole.logo} alt={`Logo ${pole.name}`} data-testid="pole-logo" className={pole.slug === "heels-compagnie" ? "max-w-[240px] sm:max-w-[300px] max-h-52 object-contain mb-6 drop-shadow-[0_10px_30px_rgba(0,0,0,0.7)]" : "max-w-[320px] sm:max-w-md max-h-40 object-contain bg-white rounded-full px-8 py-5 mb-6 shadow-[0_14px_50px_rgba(0,0,0,0.5)] ring-1 ring-white/60"} />
          )}
          <span className="he-overline text-white/70">Pôle Heel’Events</span>
          <h1 className="font-heading text-5xl sm:text-6xl font-black tracking-tighter uppercase mt-3">{pole.name}</h1>
          <p className="mt-3 text-white/80 max-w-xl">{pole.tagline}</p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16 font-body text-neutral-700 space-y-5">
        {pole.long.map((t, i) => <p key={i} className="text-lg">{t}</p>)}

        {pole.slug === "afrolat-studio" && (
          <div className="pt-6 flex flex-wrap gap-3">
            <Link to="/cours" className="btn-red rounded-full px-6 py-3 text-sm inline-flex items-center gap-2">Voir les cours <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/planning" className="rounded-full px-6 py-3 text-sm border border-black/20 hover:bg-black hover:text-white transition-colors">Planning 2026-2027</Link>
            <a href={INSCRIPTIONS_URL} target="_blank" rel="noreferrer" className="rounded-full px-6 py-3 text-sm border border-black/20 hover:bg-black hover:text-white transition-colors">S’inscrire</a>
          </div>
        )}

        {pole.slug === "heels-compagnie" && (
          <div className="pt-6 flex flex-wrap gap-3">
            <Link to="/contact" className="btn-red rounded-full px-6 py-3 text-sm">Nous contacter pour une prestation</Link>
            <Link to="/evenements" className="rounded-full px-6 py-3 text-sm border border-black/20 hover:bg-black hover:text-white transition-colors">Voir nos spectacles</Link>
          </div>
        )}

        {(pole.slug === "solidarite" || pole.slug === "entree-des-artistes") && (
          <div className="pt-6 flex flex-wrap gap-3">
            <Link to="/contact" className="btn-red rounded-full px-6 py-3 text-sm">Nous contacter</Link>
          </div>
        )}

        {(pole.slug === "solidarite" || pole.slug === "entree-des-artistes") && (
          <div className="mt-12 p-6 border border-dashed border-black/20 rounded-xl text-sm text-neutral-500">
            Section à enrichir prochainement : projets, actualités, galerie photos/vidéos.
          </div>
        )}
      </section>

      {pole.slug === "heels-compagnie" && (
        <section data-testid="compagnie-plaquette" className="bg-[var(--he-red)] text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <p className="he-overline text-white/70">Plaquette de la Compagnie 2026</p>
              <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tight mt-1">Toutes nos prestations en un document</h2>
              <p className="mt-2 text-white/85 text-sm font-body max-w-xl">Nos 7 univers, nos références (Zénith de Paris, Canal+, Cabaret Sauvage…) et nos contacts booking — à partager avec vos équipes.</p>
            </div>
            <a href={QUEENS_SHOW.plaquette} target="_blank" rel="noreferrer" data-testid="plaquette-download-main" className="rounded-full px-7 py-3.5 bg-white text-black text-sm font-bold hover:bg-black hover:text-white transition-colors inline-flex items-center gap-2 shrink-0">
              <Download className="w-4 h-4" /> Télécharger la plaquette (PDF)
            </a>
          </div>
        </section>
      )}

      {pole.slug === "heels-compagnie" && (
        <section data-testid="compagnie-jda" className="bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="grid grid-cols-2 gap-3 lg:order-1">
              {JDA_SHOW.photos.map((p, i) => (
                <img key={p} src={p} alt={`J'ai Deux Amours — photo ${i + 1}`} data-testid={`jda-photo-${i + 1}`} className="rounded-xl object-cover object-top w-full aspect-[4/3]" />
              ))}
            </div>
            <div className="lg:order-2">
              <p className="he-overline text-neutral-500">Notre revue spectacle</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter uppercase mt-3">{JDA_SHOW.name}</h2>
              <p className="mt-2 text-[var(--he-red)] font-bold text-sm uppercase tracking-widest">{JDA_SHOW.tagline}</p>
              <p className="mt-5 text-neutral-700 font-body">{JDA_SHOW.description}</p>
              <ul className="mt-6 space-y-2 text-sm text-neutral-700 font-body">
                {JDA_SHOW.points.map((pt) => (
                  <li key={pt} className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[var(--he-red)] shrink-0" /> {pt}</li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a href={`mailto:${JDA_SHOW.contact}`} data-testid="jda-contact-btn" className="btn-red rounded-full px-6 py-3 text-sm">
                  Devis & booking
                </a>
              </div>
              <p className="mt-4 text-xs text-neutral-500 font-body">{JDA_SHOW.duration} · {JDA_SHOW.price} · {JDA_SHOW.contact} · {JDA_SHOW.phone} · Instagram @{JDA_SHOW.instagram}</p>
            </div>
          </div>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-16 sm:pb-20 grid grid-cols-1 md:grid-cols-2 gap-5">
            <figure data-testid="jda-video" className="rounded-2xl overflow-hidden border border-black/10">
              <div className="aspect-video bg-black">
                <video src="/videos/jda.mp4" controls preload="metadata" className="w-full h-full" />
              </div>
              <figcaption className="p-3 text-sm text-neutral-500 font-body">J’ai Deux Amours — extrait vidéo de la revue</figcaption>
            </figure>
            <figure data-testid="jda-video-teaser" className="rounded-2xl overflow-hidden border border-black/10">
              <div className="aspect-video bg-black">
                <video src="/videos/jda-teaser-2.mp4" controls preload="metadata" className="w-full h-full" />
              </div>
              <figcaption className="p-3 text-sm text-neutral-500 font-body">J’ai Deux Amours — teaser</figcaption>
            </figure>
          </div>
        </section>
      )}

      {pole.slug === "heels-compagnie" && (
        <section data-testid="compagnie-queens" className="bg-black text-white he-grain">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="he-overline text-white/50">Notre revue cabaret</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter uppercase mt-3">{QUEENS_SHOW.name}</h2>
              <p className="mt-2 text-[var(--he-red)] font-bold text-sm uppercase tracking-widest">{QUEENS_SHOW.tagline}</p>
              <p className="mt-5 text-white/80 font-body">{QUEENS_SHOW.description}</p>
              <p className="mt-3 text-white/60 text-sm font-body italic">{QUEENS_SHOW.collab}</p>
              <ul className="mt-6 space-y-2 text-sm text-white/80 font-body">
                {QUEENS_SHOW.points.map((pt) => (
                  <li key={pt} className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[var(--he-red)] shrink-0" /> {pt}</li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a href={`mailto:${QUEENS_SHOW.contact}`} data-testid="queens-contact-btn" className="rounded-full px-6 py-3 text-sm border border-white/30 hover:bg-white hover:text-black transition-colors">
                  Devis & booking
                </a>
              </div>
              <p className="mt-4 text-xs text-white/50 font-body">{QUEENS_SHOW.price} · {QUEENS_SHOW.contact} · {QUEENS_SHOW.phone} · Instagram @{QUEENS_SHOW.instagram}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {QUEENS_SHOW.photos.map((p, i) => (
                <img key={p} src={p} alt={`Queens — photo ${i + 1}`} data-testid={`queens-photo-${i + 1}`} className={`rounded-xl object-cover object-top w-full ${i === QUEENS_SHOW.photos.length - 1 ? "col-span-2 aspect-[21/9]" : "aspect-[4/3]"}`} />
              ))}
            </div>
          </div>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-16 sm:pb-20">
            <figure data-testid="queens-video" className="rounded-2xl overflow-hidden border border-white/10">
              <div className="aspect-video bg-black">
                <video src="/videos/queens.mp4" controls preload="metadata" className="w-full h-full" />
              </div>
              <figcaption className="p-3 text-sm text-white/60 font-body">Queens — extrait vidéo de la revue</figcaption>
            </figure>
          </div>
        </section>
      )}

      {pole.slug === "heels-compagnie" && (
        <section data-testid="compagnie-prestations" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <p className="he-overline text-neutral-500">Autres prestations</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Nos univers sur mesure</h2>
          <p className="mt-3 text-neutral-600 font-body max-w-2xl">
            Six autres revues et shows adaptables à toutes les configurations : comités d’entreprise, théâtres, casinos, cabarets, structures jeune public, événements privés. Tarifs sur devis.
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {COMPAGNIE_UNIVERS.map((u) => (
              <div key={u.name} data-testid="prestation-card" className="border border-black/10 rounded-2xl p-5 hover:border-[var(--he-red)] transition-colors">
                <p className="he-overline text-neutral-500">{u.format}</p>
                <h3 className="font-heading text-xl font-bold mt-2">{u.name}</h3>
                <p className="mt-2 text-sm text-neutral-600 font-body">{u.desc}</p>
                <p className="mt-3 text-xs font-bold uppercase tracking-widest text-[var(--he-red)]">{u.duration} · sur devis</p>
              </div>
            ))}
          </div>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-3 gap-3">
            {PRESTATION_PHOTOS.map((p, i) => (
              <img key={p} src={p} alt={`Heel'Events la Compagnie — prestation ${i + 1}`} data-testid={`prestation-photo-${i + 1}`} className="rounded-xl object-cover w-full aspect-[4/3] hover:scale-[1.02] transition-transform duration-500" />
            ))}
            <div data-testid="prestation-video" className="rounded-xl overflow-hidden aspect-[4/3] bg-black">
              <video src={PRESTATION_VIDEO} controls preload="metadata" className="w-full h-full object-cover" />
            </div>
            <a href={`mailto:${QUEENS_SHOW.contact}?subject=Demande de devis — Prestation Heel'Events la Compagnie`} data-testid="prestations-devis-btn" className="rounded-xl bg-black text-white flex flex-col items-center justify-center gap-2 aspect-[4/3] hover:bg-[var(--he-red)] transition-colors">
              <span className="font-heading text-xl font-black">Votre événement ?</span>
              <span className="text-xs uppercase tracking-widest font-bold">Demander un devis</span>
            </a>
          </div>
        </section>
      )}

      {videos.length > 0 && (
        <section data-testid={`${pole.slug}-videos`} className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 sm:pb-20">
          <p className="he-overline text-neutral-500">En images</p>
          <h2 className="font-heading text-3xl font-bold tracking-tight mt-2">Extraits & répétitions</h2>
          {videoGroups.map((g) => (
            <div key={g.name || "autres"} className="mt-8" data-testid={`video-group-${g.name ? g.name.toLowerCase().replace(/[^a-z0-9]+/g, "-") : "autres"}`}>
              {g.name && (
                <h3 className="font-heading text-xl font-bold tracking-tight flex items-center gap-3">
                  {g.name}<span className="h-px flex-1 bg-black/10" />
                </h3>
              )}
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-5">
                {g.items.map((v) => (
                  <figure key={v.id} className="rounded-xl overflow-hidden border border-black/10">
                    <div className="aspect-video bg-black">
                      <video src={v.url} controls preload="metadata" className="w-full h-full" />
                    </div>
                    <figcaption className="p-3 text-sm font-body">{v.title}</figcaption>
                  </figure>
                ))}
              </div>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
